import {document, window} from '../polyfill';
import debug from 'debug';

import type {Article, QuantitativeValue, ImageObject, Organization, VideoObject} from 'schema-dts';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import {hydrate} from 'inferno-hydrate';
import {Router} from 'inferno-router';
import type {VNode} from 'inferno';

import {createBrowserHistory} from 'history';

import {addFbq, addGA, addGA4} from './no-ts';
import {capitalize, capitalizeAll} from './capitalize';
import {isMobile, iOS} from './isMobile';
import {isStatic} from './isStatic';
import {isStaticImage} from './isStaticImage';
import {isSticker} from './isSticker';
import {safelySerializeJSON} from './safelySerializeJSON';
import {StructuredConsole} from './structured-logging';
import tryDecodeURI from './tryDecodeURI';

// schema-dts's WithContext is broken.
export declare type WithContext<T> = T & {
    '@context': string;
};

export const appDebug = debug('app');
const moduleDebug = appDebug.extend('util');

export function encodeHeaderVal(val: any): string {
    return val.replace(/./g, function(c) {
        return c.charCodeAt(0) < 128 ? c : `\\x${c.charCodeAt(0).toString(16)}`;
    });
}

function setGaSessionDimension(newObj: Record<string, any>) {
    window.ga('set', newObj);
    // gtag doesn't have a way of setting session scoped cusotm dimansions,
    // so we have to keep our own list.
    Object.assign(window.gaSessionDimensions, newObj);
}


type GAEventParams = {
    name?: string;
    category?: string;
    action?: string;
    label?: string;
    value?: string|number|boolean;
};
function gaEvent({name, category, action, label, value}: GAEventParams) {
    window.ga('send', 'event', {
        eventName: name,
        eventCategory: category,
        eventAction: action,
        eventLabel: label,
        eventValue: value,
    });
}

type GATimingParams = {
    name: string;
    category: string;
    value: number;
};
function gaTiming({category, name, value}: GATimingParams) {
    window.gtag('event', 'timing_complete', {
        event_category: category,
        name,
        value,
        ...window.gaSessionDimensions,
    });
}

export function getIdForUser(user: api.User|null): string|null {
    return user ? (user.id || user.profile_id) : null;
}


/**
 * Used for log output that verifies app state during tests. If these
 * logs are silenced tests and possibly the application may break.
 */
export function testLogging(s: string) {
    if (process.env.BROWSER) {
        window.testCounter = window.testCounter || {};
        window.testCounter[s] = window.testCounter[s] || 0;
        window.testCounter[s]++;
    }
}


/**
 *
 */
export function noop() {}


// At a certain point, should just switch to humanize-plus.


/**
 *
 * @param {string} s An ID from a /view/:id URL.
 */
export function parseViewId(s: string): string|null {
    // Find the last number in the URL.
    const m = s.match(/.*?(\d+)[^\d]*$/);
    if (m && m[1]) {
        return m[1];
    }

    return null;
}


const _alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('');
const _alphabetMap = {};
for (let i = 0; i < _alphabet.length; i++) {
    _alphabetMap[_alphabet[i]] = BigInt(i);
}
Object.freeze(_alphabetMap);


/**
 *
 * @param {string} s A short ID from a /:shortId*.gif URL.
 */
export function parseViewShortId(s: string): string {
    const len = BigInt(_alphabet.length);
    let n = BigInt(0);
    for (const char of s) {
        if (!Object.hasOwn(_alphabetMap, char)) {
            return '';
        }
        n = n * len + _alphabetMap[char];
    }
    return n.toString();
}


export function escapeQuotesOnServer(s: string): string {
    return process.env.BROWSER ? s : s.replace(/"/g, '&quot;');
}

export function intComma(num: number): string {
    const numStr = num.toString();
    const ary = [];
    let start;
    for (let i = numStr.length, len = 3; i > 0; i -= 3) {
        start = i - 3;
        if (start < 0) {
            len += start;
            start = 0;
        }
        ary.unshift(numStr.substr(start, len));
    }
    return ary.join(',');
}

export function intAbbreviation(num: number): string {
    if (num < 1000) {
        return num.toString();
    }
    if (num < 1000000) {
        return `${Math.floor(num / 1000)}K`;
    }
    if (num < 1000000000) {
        return `${Math.floor(num / 1000000)}M`;
    }
    return `${Math.floor(num / 1000000000)}B`;
}


export function reflect<T>(promise: Promise<T>): Promise<T> {
    return promise.catch(function reflectInner(error) {
        console.error('catch', error);
        return error;
    });
}

// NB: static stickers are currently treated the same as regular stickers
export function isStaticSticker(gif: api.Post): boolean {
    return isStatic(gif) && isSticker(gif);
}

export function scrollbarVisible() {
    const htmlElement = document.documentElement;
    const scrollHeight = htmlElement.scrollHeight;
    const clientHeight = htmlElement.clientHeight;
    return scrollHeight > clientHeight;
}

export function isV2PostId(id: string): boolean {
    if (!id) {
        return false;
    }
    const front = id.slice(0, id.length - 9);
    const end = id.slice(id.length - 9);
    return Boolean(front) || parseInt(end) >= 200000000;
}

export function getV1PostId(gif: api.Post): string|null {
    if (gif?.legacy_info?.post_id) {
        return gif.legacy_info.post_id;
    }
    if (gif && !isV2PostId(gif.id)) {
        return gif.id;
    }
    return null;
}

export function getCanonicalPostId(gif: api.Post): string|null {
    if (gif) {
        if (gif.itemurl.endsWith(gif.id)) {
            return gif.id;
        }
        const v1ID = getV1PostId(gif);
        if (gif.itemurl.endsWith(v1ID)) {
            return v1ID;
        }
    }
    return null;
}

export function isTouchDevice() {
    return Boolean(process.env.BROWSER && window && navigator) && (
        ('ontouchstart' in window) ||
        (navigator.maxTouchPoints > 0) ||
        // @ts-expect-error: non-standard property
        (navigator.msMaxTouchPoints > 0)
    );
}


export function downloadBlob(blob: Blob, filename: string) {
    const objectUrl = URL.createObjectURL(blob);
    downloadObjectUrl(objectUrl, filename);
}

export function downloadObjectUrl(objectUrl: string, filename: string) {
    const a = document.createElement('a');
    a.download = filename;
    a.href = objectUrl;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
}


export class FrontEndRequest {
    console: StructuredConsole;
    preRendering: boolean;
    protocol: string;
    hostname: string;
    accept: string;
    preferredType: string;
    restrictedCountryCode: string;
    localLogReqId: number;

    constructor() {
        this.console = new StructuredConsole({});
        this.preRendering = false;
        this.protocol = '';
        this.hostname = '';
        this.accept = '';
        this.preferredType = '';
        this.restrictedCountryCode = '';
        this.localLogReqId = -1;
    }
}


export class FrontEndResponse {
    status: number;
    headers: Headers;
    body: null|string;
    locals: object;
    htmlMetadata: VNode[];

    constructor() {
        this.status = 200;
        this.headers = new Headers();
        this.body = null;
        this.reset();
        this.locals = {cspNonce: ''};
        this.htmlMetadata = [];
    }
    reset() {
        this.status = 200;
        this.headers = new Headers();
        this.headers.set('content-type', 'text/html; charSet=utf-8');
        this.body = null;
    }
    vary(header: string) {
        this.headers.append('vary', header);
    }
    preload(url: string, asType: string, push: boolean) {
        this.headers.append('link',
            `<${url}>; rel=preload; as=${asType}${!push ? '; nopush' : ''}`);
    }
}

export function localURL(urlStr: string, includeSearch?: boolean) {
    const url = new URL(urlStr);
    let s = url.pathname;
    try {
        s = decodeURI(s);
    } catch (e) {
        // ignore
    }
    if (includeSearch && url.search) {
        s += url.search;
    }
    return s;
}

export function fullURL(CONSTANTS: app.PublicConstants, url: string): string {
    return CONSTANTS.BASE_URL + url;
}


export function cleanMultiwordTag(tag: string) {
    if (tag.includes(' ')) {
        // This tag has already been split on the backend.
        return tag.trim();
    }
    if (tag.includes('-')) {
        // This tag has already been split on the backend.
        return tag.replace(/-/g, ' ').trim();
    }
    if (tag.match(/^[A-Z0-9]+s?$/)) {
        return tag.trim();
    }
    return tag.replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/\s+/g, ' ')
        .trim();
}

export function removeEmptyStrings(ary: string[]): string[] {
    const tmpAry = [];
    for (const s of ary) {
        if (s.length) {
            tmpAry.push(s);
        }
    }
    return tmpAry;
}


export function cleanTermsForURL(ary: string[]): string[] {
    for (let i = 0; i < ary.length; i++) {
        ary[i] = ary[i].toLowerCase();
    }
    return ary;
}


export type OrganizationJsonLD = WithContext<Organization>;
export type ImageJsonLD = WithContext<ImageObject>;
export type VideoJsonLD = WithContext<VideoObject>;
export type ArticleJsonLD = WithContext<Article & {
    publisher: OrganizationJsonLD,
    image?: ImageJsonLD,
    video?: VideoJsonLD,
}>;


export function getArticleLDJSON(that: any, gif: api.Post): ArticleJsonLD {
    let created = new Date().toISOString();
    let author = 'unknown';
    const title = that.title;
    const keywords = that.keywords;
    const description = that.description;
    const canonicalURL = that.canonicalURL;

    if (gif) {
        created = new Date(gif.created * 1000).toISOString();
        if (gif.user?.username) {
            author = gif.user.username;
        }
    }

    const data: ArticleJsonLD = {
        '@context': 'http://schema.org',
        '@type': 'Article',
        'author': author,
        'creator': author,
        'headline': title,
        'name': title,
        'url': canonicalURL,
        'mainEntityOfPage': canonicalURL,
        'keywords': keywords,
        'dateModified': created,
        'datePublished': created,
        'publisher': {
            '@context': 'http://schema.org',
            '@type': 'Organization',
            'name': 'Tenor',
            'logo': {
                '@type': 'ImageObject',
                'url': that.fullURL('/assets/img/tenor-app-icon.png'),
            },
            'sameAs': [
                'https://twitter.com/gifkeyboard',
                'https://www.facebook.com/tenorapp/',
                'https://www.linkedin.com/company/tenorinc/',
            ],
        } as OrganizationJsonLD,
    };
    if (gif) {
        const mediaFormats = gif.media_formats;
        if (mediaFormats.gif) {
            data.image = {
                '@context': 'http://schema.org',
                '@type': 'ImageObject',
                'author': author,
                'creator': author,
                'name': title,
                'keywords': keywords,
                'description': description,
                'url': canonicalURL,
                'contentUrl': mediaFormats.gif.url,
                'thumbnailUrl': mediaFormats.gifpreview.url,
                'embedUrl': that.embedUrl,
                'width': (mediaFormats.gif.dims[0] as
                    unknown as QuantitativeValue),
                'height': (mediaFormats.gif.dims[1] as
                    unknown as QuantitativeValue),
                'dateCreated': created,
                'uploadDate': created,
                'representativeOfPage': true,
            };
        }
        if (mediaFormats.mp4) {
            data.video = {
                '@context': 'http://schema.org',
                '@type': 'VideoObject',
                'author': author,
                'creator': author,
                'name': title,
                'keywords': keywords,
                'description': description,
                'url': canonicalURL,
                'contentUrl': mediaFormats.mp4.url,
                'thumbnailUrl': mediaFormats.gifpreview.url,
                'embedUrl': that.embedUrl,
                'width': (mediaFormats.mp4.dims[0] as
                    unknown as QuantitativeValue),
                'height': (mediaFormats.mp4.dims[1] as
                    unknown as QuantitativeValue),
                'dateCreated': created,
                'uploadDate': created,
                'duration': `PT0M${Math.ceil(mediaFormats.mp4.duration)}S`,
            };
        }
    }
    return data;
}


export function getRouter(routes, history) {
    return (
        <Router history={ history }>
            { routes }
        </Router>
    );
}


export function runIfInBrowser(routes, promises?: Promise<any>[]) {
    promises = promises || [];
    const functionDebug = moduleDebug.extend('runIfInBrowser');
    functionDebug('checking');
    if (process.env.BROWSER && window) {
        const start = function start() {
            functionDebug('start');
            // load from window vars
            // might be relevant: https://github.com/ReactTraining/react-router/blob/master/docs/guides/ServerRendering.md#async-routes
            const config: Omit<app.AllConstants, 'PRIVATE'> = {
                PUBLIC: JSON.parse(atob(
                    document.getElementById('data').innerHTML)),
                locale: 'en',
                localeData: null,
            };

            const localeDataEl = document.getElementById('locale-data');
            if (localeDataEl) {
                config.locale = localeDataEl.dataset.locale;
                config.localeData = JSON.parse(localeDataEl.innerHTML);
            }

            const root = document.getElementById('root');

            const history = createBrowserHistory();
            history.listen(function historyListener() {
                window.scroll(0, 0);
            });

            let firstLocationSet = true;

            function locationSet(): void {
                if (firstLocationSet) {
                    firstLocationSet = false;
                } else {
                    console.groupEnd();
                }
                console.groupCollapsed(
                    `locationSet: ${
                        history.location.pathname
                    }${
                        history.location.search
                    }`);
            }
            history.listen(locationSet);
            locationSet();

            const requestObj = new FrontEndRequest();
            requestObj.hostname = window.location.hostname;
            requestObj.protocol = window.location.protocol.split(':')[0];

            const router = routes(
                history, config, requestObj, new FrontEndResponse(), {});

            hydrate(router, root);
        };
        const callStartOnLoad = function callStartOnLoad() {
            if (/comp|inter|loaded/.test(document.readyState)) {
                start();
            } else {
                document.addEventListener('DOMContentLoaded', start);
            }
        };
        Promise.all(promises).then(callStartOnLoad);
    }
}

export function setupAnalytics(
    options: {context: jsx.AppGlobals, ga?: boolean, fbq?: boolean}) {
    const functionDebug = moduleDebug.extend('setupAnalytics');
    functionDebug('incoming options', options);
    options = {
        ga: true,
        fbq: false,
        ...options,
    };
    const context = options.context;
    const CONSTANTS = context.CONSTANTS;

    /*
    Filter out browser internal errors: https://groups.google.com/a/chromium.org/forum/#!topic/chromium-discuss/7VU0_VvC7mE
    Message: TypeError:
        undefined is not an object (evaluating '__gCrWeb.autofill.extractForms')
        Line: 1 - Column: 85 - Error object: {"line":1,"column":85,"sourceURL":"https://tenor.com/search/tulla-gifs"} - URL: https://tenor.com/search/tulla-gifs
    Message: Uncaught ReferenceError:
        vid_mate_check is not defined
        Line: 1 - Column: 1 - Error object: {} - URL:
    Message: Uncaught ReferenceError:
        night_mode_disable is not defined
        Line: 1 - Column: 1 - Error object: {} - URL:
    Message: Uncaught ReferenceError:
        fixedTimeID is not defined
        Line: 1 - Column: 4 - Error object: {} - URL:
    Message: Uncaught ReferenceError:
        ztePageScrollModule is not defined
        Line: 1 - Column: 957 - Error object: {} - URL:
    Message: Uncaught TypeError:
        Cannot set property 'tgt' of null
        Line: 34384 - Column: 25 - Error object: {} - URL: https://tenor.com/search/slide-gifs
    Message: Uncaught ReferenceError:
        diableNightMode is not defined
        Line: 1 - Column: 1 - Error object: {} - URL:
    Message: Uncaught ReferenceError:
        androidOnPause is not defined
        Line: 1 - Column: 2 - Error object: {} - URL:
    Message: Uncaught SecurityError:
        Failed to execute 'sendBeacon' on 'Navigator': Refused to send beacon to 'http://gj.track.uc.cn/collect?...' because it violates the document's Content Security Policy.
    Message: Uncaught SyntaxError:
        Failed to execute 'querySelectorAll' on 'Document': 'div:has(>
            iframe[id='198230182308109283091823098102938908128390'])'
            is not a valid selector.
        Error object: {"stack":"Error: Failed to execute 'querySelectorAll' on
            'Document': 'div:has(>
            iframe[id='198230182308109283091823098102938908128390'])' is not a
            valid selector.\n at <anonymous>:54:97"}
        URL:https://tenor.com/search/cat-game-gifs,Line:54,Column:97
    */
    const ignoredErrors = [
        '__gCrWeb',
        'vid_mate_check',
        'night_mode_disable',
        'fixedTimeID',
        'ztePageScrollModule',
        'Cannot set property \'tgt\' of null',
        'diableNightMode is not defined',
        'androidOnPause is not defined',
        'gj.track.uc.cn',
        `Failed to execute 'querySelectorAll' on 'Document': 'div:has(> iframe[id=`, // eslint-disable-line max-len
    ];

    if (process.env.BROWSER && options['ga']) {
        addGA(window);
        addGA4(window);
    } else {
        window.ga = noop;
        window.gtag = noop;
    }
    window.gaSessionDimensions = {};
    window.setGaSessionDimension = setGaSessionDimension;
    window.gaEvent = gaEvent;
    window.gaTiming = gaTiming;

    window.gtag('config', 'G-EFZJPJ84N1', {
        'debug_mode': true,
        'send_page_view': false,
        'RELEASE_TAG': CONSTANTS.RELEASE_TAG,
    });

    window.ga('create', CONSTANTS.GA_ID || 'UA-3371002-34');
    window.ga('require', 'linkid');
    window.ga('require', 'displayfeatures');

    if (process.env.BROWSER && options['fbq']) {
        functionDebug('adding fbq');
        addFbq(window);
    } else {
        window.fbq = noop;
    }

    window.fbq('init', CONSTANTS.FB_ID || '1490938941233674');

    window.onerror =
    function windowOnError(msg, url, lineNo, columnNo, error, ...args) {
        console.error(msg, url, lineNo, columnNo, error, ...args);
        if (msg && typeof msg === 'string') {
            if (url.startsWith('chrome-extension://')) {
                return;
            }
            for (const ignoredError of ignoredErrors) {
                if (msg.includes(ignoredError)) {
                    return;
                }
            }
        }

        window.gtag('event', 'exception', {
            description: msg,
            fatal: false,
            url,
            lineNo,
            columnNo,
            errObj: error ?
                JSON.stringify(error, Object.getOwnPropertyNames(error)) :
                error,
            ...window.gaSessionDimensions,
        });
    };

    window.addEventListener('beforeinstallprompt',
        function beforeinstallprompt(e) {
            window.gaEvent({
                category: 'Native Banner',
                action: 'beforeinstallprompt',
                label: e.platforms.join(','),
            });
            e.userChoice.then(function userChoiceThen(choiceResult) {
                window.gaEvent({
                    category: 'Native Banner',
                    action: 'beforeinstallprompt-userChoice',
                    label: `${choiceResult.outcome} ${choiceResult.platform}`,
                });
            });
        },
    );

    if (process.env.BROWSER) {
        const performance = window.performance;

        const perfMetricIsLogged = {
            responseStart: false,
            loadEventStart: false,
        };
        const processNavigationPerf = (navTimings) => {
            for (const key in navTimings) {
                // Any of these values that are a number shouldn't be negative.
                if (typeof navTimings[key] === 'number' && navTimings[key] < 0) {
                    context.apiService.setIsLowQualityToTrue(
                        `${key}=${navTimings[key]}`);
                }
            }
            if (navTimings.responseStart &&
                !perfMetricIsLogged.responseStart) {
                perfMetricIsLogged.responseStart = true;
                if (navTimings.responseStart <= 0) {
                    context.apiService.setIsLowQualityToTrue(
                        `responseStart=${navTimings.responseStart}`);
                }
                context.apiService.registerEvent('perf_response_start', {
                    timing_milliseconds: navTimings.responseStart,
                });
                window.gaTiming({
                    category: 'window',
                    name: 'responseStart',
                    value: navTimings.responseStart,
                });
            }
            if (navTimings.loadEventStart &&
                !perfMetricIsLogged.loadEventStart) {
                perfMetricIsLogged.loadEventStart = true;
                const ttfbToLoad = navTimings.loadEventStart -
                    navTimings.responseStart;
                if (ttfbToLoad <= 0) {
                    context.apiService.setIsLowQualityToTrue(
                        `ttfbToLoad=${ttfbToLoad}`);
                }
                window.gaTiming({
                    category: 'window',
                    name: 'HTML TTFB to window.load Event',
                    value: ttfbToLoad,
                });
                context.apiService.registerEvent('perf_response_start_to_window_load', {
                    timing_milliseconds: ttfbToLoad,
                });

                if (context.apiService.isLowQuality) {
                    // If responseStart is negative, something's gone wrong, so
                    // report more details.
                    // max length 150 characters in GA, so we want to keep it
                    // as short as possible.
                    const ary = [performance.timeOrigin];
                    [
                        'startTime',
                        'connectStart',
                        'requestStart',
                        'responseStart',
                        'responseEnd',
                        'loadEventStart',
                        'activationStart',
                        'type',
                        'deliveryType',
                    ].forEach((k) => {
                        // max 2 decimal places, remove trailing zero decimals.
                        const value = navTimings[k];
                        ary.push(typeof value === 'number' ?
                            +value.toFixed(2) : value);
                    });
                    const msg = ary.join(',');
                    window.onerror(`-t:${msg}`, window.location.href);
                }
            }
        };

        if (performance) {
            processNavigationPerf(performance.getEntriesByType('navigation')[0]);
            new PerformanceObserver((list, obj) => {
                list.getEntries().forEach(processNavigationPerf);
            }).observe({
                type: 'navigation',
                buffered: true,
            });

            new PerformanceObserver((list, obj) => {
                list.getEntries().forEach((entry) => {
                    if (['first-contentful-paint'].includes(entry.name)) {
                        const eventName = `perf_${entry.name.replaceAll('-', '_')}`;
                        context.apiService.registerEvent(eventName, {
                            timing_milliseconds: entry.startTime,
                        });
                        window.gtag('event', 'timing', {
                            event_category: 'window',
                            name: entry.name,
                            value: entry.startTime,
                        });
                    }
                });
            }).observe({
                type: 'paint',
                buffered: true,
            });

            new PerformanceObserver((list, obj) => {
                list.getEntries().forEach((entry) => {
                    if (['largest-contentful-paint'].includes(entry.entryType)) {
                        const eventName = `perf_${entry.entryType.replaceAll('-', '_')}`;
                        context.apiService.registerEvent(eventName, {
                            timing_milliseconds: entry.startTime,
                        });
                        window.gtag('event', 'timing', {
                            event_category: 'window',
                            name: 'largest-contentful-paint',
                            value: entry.startTime,
                        });
                    }
                });
            }).observe({
                type: 'largest-contentful-paint',
                buffered: true,
            });
        }

        let windowLoaded = false;
        window.addEventListener('load', function windowLoad() {
            windowLoaded = true;
            // Try again just in case the PerformanceObserver didn't fire.
            performance?.getEntriesByType('navigation').forEach(
                processNavigationPerf);
        });
        [0, 5, 10, 15, 20].forEach((seconds: number) => {
            window.setTimeout(() => {
                window.gaEvent({
                    category: 'Page Speed',
                    action: `${seconds}s passed`,
                    label: windowLoaded ? 'loaded' : 'not loaded',
                });
            }, seconds * 1000);
        });
    }
}


const htmlElement = process.env.BROWSER ? document.body.parentElement : null;

/**
 * Make a best effort to disbale naviagtion gestures and prompt users before
 * navigating away.
 */
export function discouragePageNavigation(message?: string): Function {
    message = message || 'Are you sure you want to leave this page?';
    /**
     * This isn't always honored by all browsers. It gets fired when a browser
     * unloads* resources, but not during SPA nav.
     */
    function attemptToPreventPageNavigation(event: Event): string {
        event.preventDefault();
        // The spec says this can be any truthy value, including a string which
        // can be displayed to the user. tsc complains that this has to be a
        // boolean.
        (event.returnValue as unknown as string) = message;
        return message;
    }

    window.addEventListener('beforeunload', attemptToPreventPageNavigation);
    // NOTE Safari equivalent to 'beforeunload'
    window.addEventListener('pagehide', attemptToPreventPageNavigation);
    // Prevents pull down reload behavior. Doesn't work on Safari or iOS Chrome.
    if (htmlElement) {
        htmlElement.style.overscrollBehavior = 'contain';
    }

    return function unlisten() {
        window.removeEventListener(
            'beforeunload', attemptToPreventPageNavigation);
        window.removeEventListener(
            'pagehide', attemptToPreventPageNavigation);
        if (htmlElement) {
            htmlElement.style.overscrollBehavior = '';
        }
    };
}

/**
 * Returns false if:
 * 1. gif has geo restricitons and we're on the server side.
 * 2. gif has geo restricitons, we're on the client side, but country is
 * unknown.
 * 3. gif has geo restricitons, we're on the client side, but country is
 * incompatible.
 */
export function gifCountryAllowed(
    gif: api.Post, context: jsx.AppGlobals): boolean {
    if (gif?.geographic_restriction) {
        if (!process.env.BROWSER) {
            return false;
        }
        if (!context.countryCode) {
            return false;
        }
        const countryMatch = gif.geographic_restriction.countries.includes(
            context.countryCode);
        if (gif.geographic_restriction.type === 'allow' && !countryMatch) {
            return false;
        }
        if (gif.geographic_restriction.type === 'block' && countryMatch) {
            return false;
        }
    }
    return true;
}

export function promiseSingleton(func: Function): Function {
    const promises: Record<string, Promise<any>> = {};
    return function promiseSingletonInner(...args) {
        const key = args.join('-');
        let promise = promises[key];
        if (!promise) {
            promise = promises[key] = func(...args).finally((val) => {
                delete promises[key];
            });
        }
        return promise;
    };
}

export function isPromise(obj: any): boolean {
    return obj && typeof obj.then === 'function';
}

export function addAuthorizationHeaderIfPresent(
    headers: Headers, token: string) {
    if (token) {
        headers.set('authorization', `Bearer ${token}`);
    }
}

const GIF_CONFIG_ID = '49';
const PROFILE_CONFIG_ID = '52';

/**
 *
 * @param {string} configId Reporting Widget config ID
 * @param {string} cid Content ID/URL
 * @param {boolean} redirect Whether to redirect or open in new tab. Useful for
 * getting around browser restrictions on opening new tabs.
 */
export function reportContentViaReportingWidget(
    configId: string, cid: string, redirect: boolean) {
    const params = new URLSearchParams({cid, url: cid});
    const url = `https://reportingwidget.google.com/widget/${configId}?${params.toString()}`;
    if (redirect) {
        window.location.href = url;
    } else {
        const windowProxy = window.open(url, '_blank');
        if (windowProxy) {
            // Focus on the new window if successful.
            windowProxy.focus();
        } else {
            // The popup/tab was blocked, so redirect anyway.
            window.location.href = url;
        }
    }
}

/**
 *
 * @param {string} cid Content ID/URL
 * @param {boolean} redirect Whether to redirect or open in new tab. Useful for
 * getting around browser restrictions on opening new tabs.
 */
export function reportGifViaReportingWidget(cid: string, redirect: boolean) {
    return reportContentViaReportingWidget(GIF_CONFIG_ID, cid, redirect);
}

/**
 *
 * @param {string} cid Content ID/URL
 * @param {boolean} redirect Whether to redirect or open in new tab. Useful for
 * getting around browser restrictions on opening new tabs.
 */
export function reportProfileViaReportingWidget(
    cid: string, redirect: boolean) {
    return reportContentViaReportingWidget(PROFILE_CONFIG_ID, cid, redirect);
}

export function getPageType(context: jsx.AppGlobals): string|undefined {
    const path = context.gtService.delocalizeUrlPath(
        context.router.history.location.pathname);
    if (path === '/') {
        return 'featured';
    } else if (path.match(/\/search\//)) {
        return 'search';
    } else if (path.match(/\/users\//) || path.match(/\/official\//)) {
        return 'profile';
    } else if (path.match(/\/view\//)) {
        return 'related';
    }
    return;
}

type GifEventParams = {
    viewindex: number;
    rid: string;
    category: string|undefined;
    searchfilter: string;
    tag?: string;
};

export function getGifEventParams(context: jsx.AppGlobals, viewindex: number,
    gif: api.Post): GifEventParams {
    const params: GifEventParams = {
        viewindex,
        rid: gif.id,
        category: getPageType(context),
        searchfilter: gif.searchfilter,
    };
    if (params.category === 'search' ) {
        const searchTerms = context.gtService.parseURLSearchTags(
            context.router.history.location.pathname).join(' ');
        params.tag = searchTerms;
    } else if (params.category === 'profile' && gif.user?.username) {
        params.tag = `@${gif.user.username}`;
    }
    return params;
}

export function isValidUsername(username: string): boolean {
    return username.length >= 2 && username.length <= 32 &&
        Boolean(username.match(/^\w*$/));
}

export {
    document, window, capitalize, capitalizeAll, isMobile, iOS, isStatic,
    isStaticImage, isSticker, safelySerializeJSON, tryDecodeURI,
};
